import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../config/api';
import { formatDate } from '../utils/flightUtils';

interface FlightDangerZoneProps {
  flightId: string;
  aircraftIdentification: string;
  dateStart: string;
  redirectTo: string;
}

const FlightDangerZone: React.FC<FlightDangerZoneProps> = ({
  flightId,
  aircraftIdentification,
  dateStart,
  redirectTo,
}) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      const response = await apiRequest(`/flight-tracks/${flightId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        let errMsg = 'Fehler beim Löschen des Fluges.';
        try {
          const data = await response.json();
          if (data && data.message) {
            errMsg = data.message;
          }
        } catch {
          // ignore json parse error
        }
        throw new Error(errMsg);
      }

      setShowModal(false);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Löschen des Fluges.');
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="row mb-4">
        <div className="col-12">
          <div
            className="card shadow-sm"
            style={{
              borderRadius: '12px',
              border: '1px solid #f5c2c7',
              background: '#fffdfd',
            }}
          >
            <div className="card-body p-4 d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3">
              <div>
                <h5
                  style={{
                    fontWeight: 700,
                    color: '#dc3545',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  Flug löschen
                </h5>
                <p style={{ color: '#687088', fontSize: '14px', margin: 0 }}>
                  Alle zugehörigen Daten werden unwiderruflich gelöscht.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-outline-danger"
                style={{ fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap', minWidth: '130px' }}
                onClick={() => {
                  setError(null);
                  setShowModal(true);
                }}
              >
                Flug löschen
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1040,
            }}
            onClick={() => {
              if (!isDeleting) setShowModal(false);
            }}
          />

          {/* Modal Content */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '12px',
              padding: '28px',
              width: '90%',
              maxWidth: '480px',
              zIndex: 1050,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={{ fontWeight: 700, color: '#dc3545', margin: 0 }}>
                Flug unwiderruflich löschen?
              </h5>
              <button
                onClick={() => {
                  if (!isDeleting) setShowModal(false);
                }}
                disabled={isDeleting}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '22px',
                  cursor: isDeleting ? 'default' : 'pointer',
                  color: '#9aa0ad',
                  lineHeight: 1,
                }}
                aria-label="Schließen"
              >
                ×
              </button>
            </div>

            <p style={{ color: '#3d4554', fontSize: '14px', marginBottom: '14px', lineHeight: 1.5 }}>
              Soll der Flug mit <strong>{aircraftIdentification}</strong> vom <strong>{formatDate(dateStart)}</strong> wirklich gelöscht werden?
            </p>

            <div
              className="p-3 mb-3 rounded"
              style={{
                background: '#fff3cd',
                border: '1px solid #ffe69c',
                color: '#664d03',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              Diese Aktion kann nicht rückgängig gemacht werden.
            </div>

            {error && (
              <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '13px' }}>
                {error}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowModal(false)}
                disabled={isDeleting}
                style={{ fontSize: '13px', minWidth: '100px' }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
                style={{ fontWeight: 600, fontSize: '13px', minWidth: '140px' }}
              >
                {isDeleting ? 'Wird gelöscht...' : 'Endgültig löschen'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FlightDangerZone;
